import { ChangeEvent } from 'react';
import { format, isAfter, parse } from 'date-fns';

import { uniqueIdTransfer } from '@/apiCalls/uniqueIDTransferHelper';
import { trimClassname } from '@/utils/styleHelper';

import { EEventType } from '../eventType';
import { Tooltip } from '../Tooltip';

export interface ITimeInput {
    title: string;
    placeholder: string;
    onChange: (value: Date) => void;
    showErrors: boolean;
    id?: string;
    disabled?: boolean;
    value?: Date;
    minTime?: Date;
    maxTime?: Date;
    timeFormat?: string; // 'HH:mm' for 24-hour format or 'hh:mm a' for 12-hour format
    tooltipContent?: string;
}

export function TimeInput({
    title,
    placeholder,
    id,
    value,
    showErrors,
    disabled = false,
    onChange,
    minTime,
    maxTime,
    timeFormat = 'HH:mm', // Default to 24-hour format
    tooltipContent,
}: ITimeInput): JSX.Element {
    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const { value } = event.target;
        // Parse the time string using today's date as a base
        const baseDate = new Date();
        let time = parse(value, timeFormat, baseDate);

        if (maxTime && isAfter(time, maxTime)) {
            time = maxTime;
        }

        onChange(time);

        uniqueIdTransfer({
            eventType: EEventType.INPUT,
            applicationData: { id: id },
        });
    };

    function getDisplayValue(): string {
        if (!value) return '';
        if (maxTime && isAfter(value, maxTime))
            return format(maxTime, timeFormat);
        return format(value, timeFormat);
    }

    return (
        <div data-testid='input' className='py-2 mx-auto w-full max-w-md'>
            <Tooltip content={tooltipContent ?? ''}>
                <h1 className='fieldHeading'>{title}</h1>
            </Tooltip>
            <input
                className={trimClassname(
                    `appearance-none border rounded-[10px] mx-auto w-full max-w-md p-2 text-textPrimary leading-tight h-8 inputButtonFocus placeholderStyle text-center ${
                        showErrors ? errorClass : ''
                    } ${
                        disabled
                            ? 'hover:none cursor-disabled'
                            : 'hover:shadow '
                    }`
                )}
                id={id}
                type='time'
                data-testid='inputField'
                placeholder={placeholder}
                value={getDisplayValue()}
                onChange={handleChange}
                disabled={disabled}
                min={minTime ? format(minTime, timeFormat) : undefined}
                max={maxTime ? format(maxTime, timeFormat) : undefined}
                pattern={timeFormat}
                onInput={handleChange}
                onBlur={handleChange}
            />
        </div>
    );
}
export const errorClass = 'error';
